/**
 * Page: HubApiKeysPage
 * HUB-12-9 — super_admin API key management. Audit list of every tenant's
 * MCP API keys (mcp_api_keys), with one revoke action per active row. The
 * Sage MCP key is the only token surface today; rotation is tenant-owned
 * (each user re-generates from Settings → API Keys), so the hub view
 * intentionally omits a rotate action.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { hubApiKeys, type HubApiKey } from './api'
import { formatDateWithOptions } from '@/utils/dateUtils'

const P = {
  surface:   'var(--bg-surface)',
  elevated:  'var(--bg-elevated)',
  border:    'var(--border)',
  text:      'var(--text-primary)',
  textMuted: 'var(--text-secondary)',
  textDim:   'var(--text-muted)',
  danger:    'var(--st-error)',
  warning:   'var(--st-warning)',
  active:    'var(--st-active)',
}

type StatusFilter = 'all' | 'active' | 'revoked'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',     label: 'All' },
  { value: 'active',  label: 'Active' },
  { value: 'revoked', label: 'Revoked' },
]

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return formatDateWithOptions(d, { year: 'numeric', month: 'short', day: 'numeric' })
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const ms = Date.now() - new Date(iso).getTime()
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  const mo = Math.floor(day / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}

export default function HubApiKeysPage() {
  const [status, setStatus] = useState<StatusFilter>('all')
  const [tenantQ, setTenantQ] = useState('')
  const [keys, setKeys] = useState<HubApiKey[]>([])
  const [note, setNote] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<HubApiKey | null>(null)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const refresh = async () => {
    setLoading(true)
    setErr(null)
    try {
      const r = await hubApiKeys.list({ status })
      setKeys(r.keys)
      setNote(r.note ?? null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const filtered = useMemo(() => {
    const q = tenantQ.trim().toLowerCase()
    if (!q) return keys
    return keys.filter((k) =>
      k.company_name.toLowerCase().includes(q) ||
      k.user_email.toLowerCase().includes(q) ||
      k.user_name.toLowerCase().includes(q) ||
      k.display_name.toLowerCase().includes(q) ||
      k.key_prefix.toLowerCase().includes(q)
    )
  }, [keys, tenantQ])

  const summary = useMemo(() => {
    const active = keys.filter((k) => !k.revoked_at).length
    const revoked = keys.filter((k) => !!k.revoked_at).length
    const tenants = new Set(keys.map((k) => k.company_id)).size
    return { active, revoked, tenants }
  }, [keys])

  const onRevoke = async () => {
    if (!confirming) return
    setSubmitting(true)
    try {
      await hubApiKeys.revoke(confirming.id, reason)
      setConfirming(null)
      setReason('')
      await refresh()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Revoke failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: 24, color: P.text, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>API Keys</h1>
        <span style={{ fontSize: 12, color: P.textDim }}>
          MCP keys across every tenant — read + revoke only (rotation is tenant-owned)
        </span>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 12 }}>
        <SummaryChip label="Active" value={summary.active} tone="active" />
        <SummaryChip label="Revoked" value={summary.revoked} tone="muted" />
        <SummaryChip label="Tenants" value={summary.tenants} tone="muted" />
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', borderRadius: 8, border: `1px solid ${P.border}`, overflow: 'hidden' }}>
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setStatus(o.value)}
              style={{
                padding: '6px 12px',
                fontSize: 13,
                fontFamily: 'inherit',
                background: status === o.value ? P.elevated : 'transparent',
                color: status === o.value ? P.text : P.textMuted,
                border: 'none',
                borderRight: o.value !== 'revoked' ? `1px solid ${P.border}` : 'none',
                cursor: 'pointer',
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Filter tenants, users, prefix…"
          value={tenantQ}
          onChange={(e) => setTenantQ(e.target.value)}
          style={{
            padding: '6px 10px', borderRadius: 6, border: `1px solid ${P.border}`,
            background: P.surface, color: P.text, fontSize: 13, fontFamily: 'inherit',
            minWidth: 240, flex: '1 0 240px',
          }}
        />
        <button
          onClick={refresh}
          disabled={loading}
          style={{
            padding: '6px 12px', borderRadius: 6, border: `1px solid ${P.border}`,
            background: P.surface, color: P.textMuted, fontSize: 13, fontFamily: 'inherit',
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {note && (
        <div style={{ fontSize: 12, color: P.textDim, fontStyle: 'italic' }}>{note}</div>
      )}
      {err && (
        <div style={{
          padding: '8px 12px', borderRadius: 6, border: `1px solid ${P.danger}`,
          background: 'rgba(204,51,51,0.08)', color: P.danger, fontSize: 13,
        }}>{err}</div>
      )}

      {/* Table */}
      <div style={{
        background: P.surface, border: `1px solid ${P.border}`, borderRadius: 8,
        overflow: 'auto', flex: 1, minHeight: 200,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: P.elevated, color: P.textMuted, textAlign: 'left' }}>
              <Th>Status</Th>
              <Th>Tenant</Th>
              <Th>User</Th>
              <Th>Display name</Th>
              <Th>Prefix</Th>
              <Th>Scope</Th>
              <Th>Last used</Th>
              <Th>Created</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: P.textDim }}>
                  {keys.length === 0 ? 'No MCP API keys issued yet.' : 'No keys match this filter.'}
                </td>
              </tr>
            )}
            {filtered.map((k) => {
              const revoked = !!k.revoked_at
              return (
                <tr key={k.id} style={{ borderTop: `1px solid ${P.border}` }}>
                  <Td>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                      fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                      color: revoked ? P.textMuted : P.active,
                      background: revoked ? P.elevated : 'rgba(76,175,80,0.12)',
                      border: `1px solid ${revoked ? P.border : 'rgba(76,175,80,0.3)'}`,
                    }}>
                      {revoked ? 'Revoked' : 'Active'}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ color: P.text, fontWeight: 500 }}>{k.company_name || `#${k.company_id}`}</div>
                    <div style={{ fontSize: 11, color: P.textDim }}>id: {k.company_id}</div>
                  </Td>
                  <Td>
                    <div style={{ color: P.text }}>{k.user_name || k.user_email || `#${k.user_id}`}</div>
                    {k.user_name && <div style={{ fontSize: 11, color: P.textDim }}>{k.user_email}</div>}
                  </Td>
                  <Td>{k.display_name || <span style={{ color: P.textDim }}>—</span>}</Td>
                  <Td>
                    <code style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12, color: P.textMuted,
                    }}>{k.key_prefix || '—'}</code>
                  </Td>
                  <Td>
                    <span style={{
                      display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                      fontSize: 11, fontWeight: 600, color: P.textMuted,
                      background: P.elevated, border: `1px solid ${P.border}`,
                    }}>{k.scope}</span>
                  </Td>
                  <Td title={k.last_used_at ?? undefined}>
                    {k.last_used_at ? fmtRelative(k.last_used_at) : <span style={{ color: P.textDim }}>never</span>}
                  </Td>
                  <Td title={k.created_at}>{fmtDate(k.created_at)}</Td>
                  <Td>
                    {revoked ? (
                      <span style={{ fontSize: 12, color: P.textDim }} title={k.revoked_at ?? undefined}>
                        revoked {fmtRelative(k.revoked_at)}
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirming(k)}
                        style={{
                          padding: '4px 10px', borderRadius: 4,
                          border: `1px solid ${P.danger}`, color: P.danger,
                          background: 'transparent', fontSize: 12, fontFamily: 'inherit',
                          cursor: 'pointer',
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {confirming && (
        <ConfirmRevokeDialog
          k={confirming}
          reason={reason}
          setReason={setReason}
          submitting={submitting}
          onCancel={() => { if (!submitting) { setConfirming(null); setReason('') } }}
          onConfirm={onRevoke}
        />
      )}
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      padding: '8px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase',
      letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>{children}</th>
  )
}

function Td({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <td title={title} style={{ padding: '10px 12px', verticalAlign: 'top' }}>
      {children}
    </td>
  )
}

function SummaryChip({ label, value, tone }: { label: string; value: number; tone: 'active' | 'muted' }) {
  return (
    <div style={{
      padding: '8px 12px', borderRadius: 8, border: `1px solid ${P.border}`,
      background: P.surface, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 96,
    }}>
      <span style={{ fontSize: 11, color: P.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span style={{
        fontSize: 22, fontWeight: 700, lineHeight: 1,
        color: tone === 'active' ? P.active : P.text,
      }}>
        {value}
      </span>
    </div>
  )
}

function ConfirmRevokeDialog({
  k, reason, setReason, submitting, onCancel, onConfirm,
}: {
  k: HubApiKey
  reason: string
  setReason: (v: string) => void
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: P.surface, color: P.text, border: `1px solid ${P.border}`,
          borderRadius: 12, padding: 20, width: 480, maxWidth: '90vw',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Revoke API key?</h2>
        <p style={{ margin: 0, fontSize: 13, color: P.textMuted, lineHeight: 1.5 }}>
          This soft-deletes the key. Subsequent MCP requests using it will fail
          with 401. The user can issue a fresh key from <em>Settings → API Keys</em>.
        </p>
        <div style={{
          padding: 12, background: P.elevated, borderRadius: 8, fontSize: 12,
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <div><strong>Tenant:</strong> {k.company_name || `#${k.company_id}`}</div>
          <div><strong>User:</strong> {k.user_name || k.user_email}</div>
          <div><strong>Display name:</strong> {k.display_name || '—'}</div>
          <div>
            <strong>Prefix:</strong>{' '}
            <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {k.key_prefix}
            </code>
          </div>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: P.textMuted }}>
          Reason (recorded in audit log)
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Reported by tenant — leaked to public repo"
            rows={3}
            style={{
              padding: 8, borderRadius: 6, border: `1px solid ${P.border}`,
              background: P.elevated, color: P.text, fontFamily: 'inherit',
              fontSize: 13, resize: 'vertical',
            }}
          />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '8px 14px', borderRadius: 6, border: `1px solid ${P.border}`,
              background: 'transparent', color: P.text, fontSize: 13, fontFamily: 'inherit',
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            style={{
              padding: '8px 14px', borderRadius: 6, border: `1px solid ${P.danger}`,
              background: P.danger, color: '#fff', fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? 'Revoking…' : 'Revoke key'}
          </button>
        </div>
      </div>
    </div>
  )
}
